import { useState, type ReactElement } from "react";
import { Building2, Pencil } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  CardBody,
  DescriptionItem,
  DescriptionList,
  IconTile,
  Input,
  Modal,
  SettingsGroup,
  Skeleton,
  StatusPill,
  SettingsSaveBar,
} from "@playbookai/ui";
import { PRODUCT_NAME } from "@playbookai/types";
import { AdminSection } from "./AdminSection.js";
import { ME_QUERY_KEY, useAuthStore } from "../../auth/auth-store.js";
import { refreshSession, type SessionClaims } from "../../auth/session-api.js";
import {
  sameSession,
  scopeFromClaims,
  type SessionScope,
} from "../../auth/session-scope.js";
import { useLeaveGuard } from "../../settings/leave-guard.js";
import {
  SHORT_NAME_MAX,
  getTenantConfiguration,
  tenantConfigurationQueryKey,
  updateTenantProfile,
  validateDisplayName,
  validateDomain,
  validateShortName,
  type TenantConfiguration,
} from "../../api/tenant-config-api.js";
import { useDateTimeFormatter } from "../../settings/format-date-time.js";
import { AdminTimeZoneLabel } from "./AdminTimeZoneLabel.js";

/* Organization admin page — the tenant profile (PB-46, lifted here from the
   retired Workspace settings page by PB-42; PB-552/AC-04b gave shortName and
   domain real columns, so all three fields now persist through the same API).
   Tenant-scoped, admin-only via the `/admin` parent route gate
   (RequireTenantAdmin, PB-48).

   Tagline and accent colour are gone: PB-46 dropped both from this surface, and
   no server field was ever added for either. Region is omitted outright — it is
   Terraform-owned deployment config, and a user-level preference besides. */

/** The editable half of the tenant profile — one draft behind one Save. */
interface ProfileDraft {
  displayName: string;
  shortName: string;
  domain: string;
}

/* `SessionScope`, `scopeFromClaims` and `sameSession` now live in
   ../../auth/session-scope.ts — the invite workflow needs the same fence
   (PB-1597), and this predicate decides whether a credential may still be
   displayed, so it gets exactly one definition. A draft cannot exist in the
   `null` (claimless) scope: the form only renders once the admin-gated
   configuration query has resolved. */

/** A draft plus the session that authored it, so an edit can never outlive it. */
interface ScopedDraft {
  readonly scope: SessionScope;
  readonly profile: ProfileDraft;
}

const TENANT_STATUS = {
  active: { tone: "healthy", label: "Active" },
  suspended: { tone: "watch", label: "Suspended" },
  archived: { tone: "neutral", label: "Archived" },
} as const;

/** A compact, readable tenant mark. The stored short name may be six code
 * points, but the shell and this preview intentionally show at most two. */
function organizationMark(shortName: string, displayName: string): string {
  const configuredShortName = shortName.trim();
  return Array.from(configuredShortName || displayName.trim())
    .slice(0, configuredShortName ? 2 : 1)
    .join("")
    .toUpperCase();
}

export function OrganizationAdmin(): ReactElement {
  const formatter = useDateTimeFormatter();
  const { dateTimeOr } = formatter;
  const claims = useAuthStore((s) => s.claims);
  /* The CACHE is keyed by tenant alone, deliberately: two admins of one tenant
     should share the fetched configuration — Identity & directory reads the same
     key. Only in-progress edit state needs the narrower per-session scope. */
  const tenantId = claims?.tenantId ?? null;
  const scope = scopeFromClaims(claims);
  const queryClient = useQueryClient();
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);

  const tenantConfig = useQuery({
    queryKey: tenantConfigurationQueryKey(tenantId),
    queryFn: getTenantConfiguration,
  });
  const config = tenantConfig.data;

  /* One form, three fields, one Save. All three persist through the
     tenant-configuration API since PB-552, so this page no longer reads or
     writes the host settings mock at all. */
  const savedProfile: ProfileDraft | null = config
    ? {
        displayName: config.displayName,
        shortName: config.shortName ?? "",
        domain: config.domain ?? "",
      }
    : null;
  /* null means "untouched": fields show fetched values until the admin types, so
     a slow fetch is never mistaken for an empty required field.

     The draft is stamped with the session that authored it, and a stamp that no
     longer matches reads as untouched. An admin session can be replaced in place
     — `/me` refetches on window focus, and RequireTenantAdmin keeps this route
     mounted whenever the replacement is also an admin — so without the stamp one
     admin's unsaved draft stays on screen for the next one to save: across
     tenants that writes A's name onto tenant B, and within one tenant it lets B
     persist a value A typed. Deriving this instead of clearing it in an effect
     means the stale draft is never rendered, not even for the frame before the
     effect runs. */
  const [draft, setDraft] = useState<ScopedDraft | null>(null);
  const [sessionRefreshWarning, setSessionRefreshWarning] =
    useState<SessionScope | null>(null);
  const visibleSessionRefreshWarning =
    sessionRefreshWarning !== null && sameSession(sessionRefreshWarning, scope);
  const profileDraft =
    draft !== null && sameSession(draft.scope, scope) ? draft.profile : null;
  const profile = profileDraft ?? savedProfile;
  const profileErrors = profileDraft
    ? {
        displayName: validateDisplayName(profileDraft.displayName),
        shortName: validateShortName(profileDraft.shortName),
        domain: validateDomain(profileDraft.domain),
      }
    : { displayName: null, shortName: null, domain: null };
  const profileInvalid = Object.values(profileErrors).some(Boolean);
  /* Compared against trimmed values, because that is what a save sends — adding
     a trailing space is not a change worth an audited write. */
  const profileChanged =
    profileDraft !== null &&
    savedProfile !== null &&
    (Object.keys(savedProfile) as (keyof ProfileDraft)[]).some(
      (key) => profileDraft[key].trim() !== savedProfile[key],
    );
  const editProfile = (field: keyof ProfileDraft, value: string): void => {
    if (!savedProfile) return;
    setDraft({
      scope,
      profile: { ...(profileDraft ?? savedProfile), [field]: value },
    });
  };
  const saveProfile = useMutation({
    mutationFn: async ({ profile: next, scope: origin }: ScopedDraft) => {
      /* A full replace, as the API requires: every field is stated, and a blank
         optional one is sent as `null` to clear it rather than omitted.

         KNOWN GAP — last-write-wins across administrators. There is no revision
         precondition, so two admins editing this form concurrently can overwrite
         each other silently. Closing it is a CONTRACT change, not a client one:
         `docs/api/auth.yaml`'s profile PUT would have to carry an expected
         `updatedAt` (or an ETag / `If-Match`) and gain a 409/412, and the
         consumed-boundary digest moves with it. Deliberately not folded into
         this ticket — an IA move plus the PB-552 storage it needed — and
         deferred with the rest of the Organization hardening.

         The server-side write itself is safe: it takes the tenant row lock and
         re-checks admin status inside the transaction, so this is a concurrent
         EDIT problem, never a torn or unauthorised write. */
      const updated = await updateTenantProfile({
        displayName: next.displayName.trim(),
        shortName: next.shortName.trim() || null,
        domain: next.domain.trim() || null,
      });
      return { updated, origin };
    },
    // The PUT returns the refreshed configuration, so the new values and the
    // bumped updatedAt are the on-screen proof the save persisted.
    onMutate: () => setSessionRefreshWarning(null),
    onSuccess: ({
      updated,
      origin,
    }: {
      updated: TenantConfiguration;
      origin: SessionScope;
    }) => {
      /* Two different lifetimes settle here, and conflating them cost a real
         defect: an earlier fence dropped this whole result on a session change,
         server truth included.

         The save is COMMITTED, and it belongs to the tenant rather than to the
         session that submitted it. Discarding it leaves the next administrator
         holding a profile the server no longer has — and since every profile PUT
         carries all three fields, their next save writes those stale values back
         over the committed ones. Refetching rather than adopting this response is
         what makes that safe: it asks the server for the tenant's current state,
         so it cannot surface another tenant's body and cannot move the cache
         backwards if two saves resolved out of order.

         The draft is the SESSION's own — typed by `origin` — so it stays fenced.
         Read the live snapshot rather than a render closure, so the check cannot
         lag. */
      const live = scopeFromClaims(useAuthStore.getState().claims);
      if (!sameSession(origin, live)) {
        /* Compared claims-to-claims: `updated.tenantId` is the server's
           identifier for the row it wrote, which need not equal the value the
           JWT carries, so asking "is the tenant still the one I submitted
           under" keeps a single source of truth. Skipping the refetch when the
           tenant did change is not a safety guard — the key changed with it, so
           a fresh fetch has already happened — it just avoids a second GET. */
        if (origin !== null && origin.tenantId === live?.tenantId) {
          void queryClient.invalidateQueries({
            queryKey: tenantConfigurationQueryKey(live?.tenantId),
          });
        }
        return;
      }
      queryClient.setQueryData(
        tenantConfigurationQueryKey(origin?.tenantId),
        updated,
      );
      setDraft(null);

      /* Update the visible lockup immediately from the committed response,
         then refresh the server-side session in the background so a later /me
         read carries the same branding. A refresh failure must not turn a
         committed profile save into an apparent failed save. */
      queryClient.setQueryData<SessionClaims | null>(ME_QUERY_KEY, (current) =>
        current
          ? {
              ...current,
              tenantName: updated.displayName,
              tenantShortName: updated.shortName,
            }
          : current,
      );
      void (async () => {
        let refreshFailed = false;
        try {
          await refreshSession();
        } catch {
          refreshFailed = true;
        }
        // Fired, not awaited: see docs/learnings/2026-09-11-invalidatequeries-awaits-the-retry-ladder.md
        void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
        if (
          refreshFailed &&
          sameSession(origin, scopeFromClaims(useAuthStore.getState().claims))
        ) {
          setSessionRefreshWarning(origin);
        }
      })();
    },
  });
  const startProfileEdit = (): void => {
    if (!savedProfile) return;
    saveProfile.reset();
    setSessionRefreshWarning(null);
    setDraft({ scope, profile: savedProfile });
  };
  const cancelProfileEdit = (): void => {
    setDraft(null);
    saveProfile.reset();
  };
  const submitProfile = (): void => {
    if (!profileDraft || profileInvalid) return;
    saveProfile.mutate({ scope, profile: profileDraft });
  };
  /* A mutation's `isPending` and `error` belong to the component, not to the
     session that started it. Without this, a save begun by the previous session
     disables the next one's form and reports its failure over a form it never
     touched. Read through the mutation's own variables so both retire with the
     session that owns them. */
  const saveIsOurs =
    saveProfile.variables !== undefined &&
    sameSession(saveProfile.variables.scope, scope);
  const saving = saveProfile.isPending && saveIsOurs;
  const saveError = saveIsOurs ? saveProfile.error : null;
  /* Unsaved-edit guard (PB-5403). The host's leave guard owns both halves: it
     arms beforeunload for a reload or a closed tab, and the admin rail asks
     through it before a section change drops this draft. The earlier note here
     said catching an in-app move needed the whole shell on createBrowserRouter
     — it does not, because the host consults the dirty registry itself. Browser
     Back and the top-bar app tabs still move history outside the guard, the
     same documented limit the Settings host carries. */
  useLeaveGuard(profileChanged);

  return (
    <AdminSection
      icon={Building2}
      title="Organization"
      description="Manage the identity shown across this workspace."
      action={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="secondary" onClick={() => setLearnMoreOpen(true)}>
            Learn more
          </Button>
          {config && profileDraft === null ? (
            <Button variant="primary" onClick={startProfileEdit}>
              <Pencil className="h-4 w-4" aria-hidden />
              Edit profile
            </Button>
          ) : null}
        </div>
      }
      meta={null}
    >
      <SettingsGroup title="Organization profile" titleHidden>
        {tenantConfig.isPending ? (
          <Card className="mb-3">
            <CardBody className="flex flex-col gap-4">
              {/* Field-shaped so the layout doesn't jump when data lands. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Skeleton className="h-3 w-28 mb-1.5" />
                  <Skeleton className="h-9 w-full" />
                </div>
                <div>
                  <Skeleton className="h-3 w-28 mb-1.5" />
                  <Skeleton className="h-9 w-full" />
                </div>
              </div>
              <Skeleton className="h-4 w-2/3" />
            </CardBody>
          </Card>
        ) : tenantConfig.isError ? (
          <Card className="mb-3">
            <CardBody className="flex flex-col items-start gap-3">
              <Alert
                tone="critical"
                title="We couldn't load your organization settings"
              >
                {tenantConfig.error.message}
              </Alert>
              <Button
                variant="secondary"
                onClick={() => void tenantConfig.refetch()}
                disabled={tenantConfig.isFetching}
              >
                {tenantConfig.isFetching ? "Retrying..." : "Try again"}
              </Button>
            </CardBody>
          </Card>
        ) : config && profile ? (
          <div className="flex flex-col gap-4">
            <Card>
              <CardBody className="flex flex-col gap-5">
                <div className="flex flex-wrap items-center gap-4">
                  {/* Look row OK-67: the mark is IconTile lg in the info tone
                      (decision 12: teal that is not a selection is info). */}
                  <IconTile
                    data-testid="organization-brand-mark"
                    aria-label={`Organization mark ${organizationMark(profile.shortName, profile.displayName)}`}
                    size="lg"
                    tone="info"
                    className="shrink-0"
                  >
                    {organizationMark(profile.shortName, profile.displayName)}
                  </IconTile>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="truncate text-lg font-semibold text-pb-text">
                        {profile.displayName.trim() || "Organization name"}
                      </h3>
                      <StatusPill tone={TENANT_STATUS[config.status].tone}>
                        {TENANT_STATUS[config.status].label}
                      </StatusPill>
                    </div>
                    <p className="mt-1 text-sm text-pb-text-muted">
                      {profile.domain.trim() || "No company domain added"}
                    </p>
                  </div>
                  {profileDraft ? (
                    <span className="text-xs text-pb-text-muted">
                      Live preview
                    </span>
                  ) : null}
                </div>

                {profileDraft ? (
                  <div className="grid grid-cols-1 gap-4 border-t border-pb-border/70 pt-5 sm:grid-cols-2 @min-[42.5rem]/settings-page:grid-cols-3">
                    <Input
                      label="Organization name"
                      value={profile.displayName}
                      onChange={(e) =>
                        editProfile("displayName", e.target.value)
                      }
                      error={profileErrors.displayName ?? ""}
                      hint={`Shown wherever this workspace is named across ${PRODUCT_NAME}.`}
                      disabled={saving}
                    />
                    <Input
                      label="Short name"
                      value={profile.shortName}
                      onChange={(e) => editProfile("shortName", e.target.value)}
                      error={profileErrors.shortName ?? ""}
                      hint={`Up to ${SHORT_NAME_MAX} characters. The first two form the organization mark.`}
                      disabled={saving}
                    />
                    <Input
                      label="Company domain"
                      value={profile.domain}
                      onChange={(e) => editProfile("domain", e.target.value)}
                      error={profileErrors.domain ?? ""}
                      placeholder="example.com"
                      hint="Descriptive only — it does not control sign-in or prove ownership."
                      disabled={saving}
                    />
                  </div>
                ) : (
                  /* Look row OK-25: the label/value grids are DescriptionLists. */
                  <div className="border-t border-pb-border/70 pt-5">
                    <DescriptionList columns={3}>
                      <DescriptionItem label="Organization name">
                        {config.displayName}
                      </DescriptionItem>
                      <DescriptionItem label="Short name">
                        {config.shortName || "Not set"}
                      </DescriptionItem>
                      <DescriptionItem label="Company domain">
                        {config.domain || "Not set"}
                      </DescriptionItem>
                    </DescriptionList>
                  </div>
                )}

                <div className="border-t border-pb-border/70 pt-5">
                  <DescriptionList columns={4}>
                    <DescriptionItem label="Workspace key">
                      {config.slug}
                    </DescriptionItem>
                    <DescriptionItem label="Tenant ID">
                      <span className="break-all text-dense-1 text-pb-text-muted">
                        {config.tenantId}
                      </span>
                    </DescriptionItem>
                    <DescriptionItem
                      label={
                        /* The first of this list's two instants owns the zone;
                           Last updated below it is in the same one. */
                        <AdminTimeZoneLabel
                          label="Created"
                          formatter={formatter}
                          variant="inline"
                        />
                      }
                    >
                      <span className="text-pb-text-muted">
                        {dateTimeOr(config.createdAt, "—")}
                      </span>
                    </DescriptionItem>
                    <DescriptionItem label="Last updated">
                      <span className="text-pb-text-muted">
                        {dateTimeOr(config.updatedAt, "—")}
                      </span>
                    </DescriptionItem>
                  </DescriptionList>
                </div>
              </CardBody>
            </Card>
            {visibleSessionRefreshWarning ? (
              <Alert tone="watch" title="Organization saved">
                The profile is saved, but your session did not refresh. Reload
                the page so the organization name in navigation matches.
              </Alert>
            ) : null}
            {/* `saveError`, not the mutation's own error: an earlier session's
                  failure is not this one's to report. A failed save deliberately
                  keeps its draft so the admin can correct it, and Cancel resets
                  the mutation, so within one session this shows exactly while
                  there is something to fix. */}
            {saveError ? (
              <Alert tone="critical" title="Couldn't save the profile">
                {saveError.message}
              </Alert>
            ) : null}
            {/* The one Save bar every draft-based page shares (PB-5134); the
                  button keeps its "Saving..." label while the write is in
                  flight, as it always did. */}
            {profileDraft ? (
              <SettingsSaveBar
                state={saving ? "saving" : profileChanged ? "dirty" : "clean"}
                onReset={cancelProfileEdit}
                resetDisabled={saving}
                saveDisabled={profileInvalid || !profileChanged}
                onSave={submitProfile}
                resetLabel="Cancel"
                saveLabel={saving ? "Saving..." : "Save changes"}
                savedHint={`Last updated ${dateTimeOr(config.updatedAt, "—")}`}
                unsavedHint="Unsaved changes"
              />
            ) : null}
          </div>
        ) : null}
      </SettingsGroup>

      <Modal
        open={learnMoreOpen}
        onClose={() => setLearnMoreOpen(false)}
        title="Managing your organization"
        description="A quick guide to your workspace identity and organization-owned details."
        describedBy="organization-help-overview"
        size="md"
        footer={
          <Button variant="secondary" onClick={() => setLearnMoreOpen(false)}>
            Done
          </Button>
        }
      >
        <div
          id="organization-help-overview"
          className="space-y-4 text-sm text-pb-text-secondary"
        >
          <section>
            <h3 className="font-semibold text-pb-text">
              Organization identity
            </h3>
            <p className="mt-1">
              The organization name identifies this workspace throughout
              {PRODUCT_NAME}. The short name creates its compact mark in the
              shell and profile preview.
            </p>
          </section>
          <section>
            <h3 className="font-semibold text-pb-text">Editing the profile</h3>
            <p className="mt-1">
              Choose Edit profile to update the name, short name and company
              domain together. The preview responds as you type; Save changes
              publishes the new identity across the workspace.
            </p>
          </section>
          <section>
            <h3 className="font-semibold text-pb-text">Company domain</h3>
            <p className="mt-1">
              The domain is descriptive organization information. It does not
              verify ownership, restrict sign-in or configure single sign-on.
            </p>
          </section>
          <section>
            <h3 className="font-semibold text-pb-text">
              Workspace-owned details
            </h3>
            <p className="mt-1">
              The workspace key, tenant ID, status and timestamps are read-only
              identifiers managed by the platform. Share the tenant ID only when
              support or integration setup requires it.
            </p>
          </section>
        </div>
      </Modal>

      {/* Sending domains (PB-2938) remain implemented and tested, but are not
          part of the MVP Organization experience. Re-enable the panel here
          when the post-MVP email-domain workflow is scheduled. */}
    </AdminSection>
  );
}
